/**
 * Recipes Service
 *
 * API calls for Recipes feature.
 */

import { apiClient } from '@/api/client';
import type {
  RecipeWithRelations,
  RecipeListItem,
  RecipeVersionSummary,
  RecipeCostCalculation,
  PosProductWithoutRecipe,
  RecipeStats,
  PaginatedResult,
  ApiSuccess,
  RecipesListResponse,
  RecipeDetailResponse,
  RecipeCreateResponse,
  RecipeUpdateResponse,
  RecipeArchiveResponse,
  RecipeDuplicateResponse,
  RecipeVersionsResponse,
  RecipesByCodeResponse,
  RecipeCostResponse,
  RecipeStatsResponse,
  RecipeFlatViewResponse,
  PosProductsWithoutRecipeResponse,
  QueryRecipesDto,
  QueryRecipesParams,
  RecalculateAllCostsResponse,
  RecipesRecalculateAllResponse,
} from '@bstock/shared';
import type {
  CreateRecipeInput,
  UpdateRecipeInput,
  AddIngredientInput,
  UpdateIngredientInput,
  DuplicateRecipeInput,
  PublishRecipeInput,
  DeleteDraftResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  RecipeWithRelations,
  RecipeListItem,
  RecipeVersionSummary,
  RecipeCostCalculation,
  PosProductWithoutRecipe,
  RecipeStats,
  PaginatedResult,
  QueryRecipesDto,
  CreateRecipeInput,
  UpdateRecipeInput,
  AddIngredientInput,
  UpdateIngredientInput,
  DuplicateRecipeInput,
  PublishRecipeInput,
  DeleteDraftResponse,
  RecalculateAllCostsResponse,
};

export const recipesService = {
  /**
   * List recipes with pagination and filters
   * Sprint 6: Added includeDrafts and hasDraft params
   */
  async list(params: QueryRecipesDto): Promise<RecipesListResponse> {
    // Transform boolean to string for query params
    const queryParams: QueryRecipesParams = {
      ...params,
      hasProduct: params.hasProduct?.toString(),
      includeArchived: params.includeArchived?.toString(),
      includeDrafts: params.includeDrafts?.toString(),
      hasDraft: params.hasDraft?.toString(),
    };
    const response = await apiClient.get<ApiSuccess<RecipesListResponse>>('/recipes', { params: queryParams });
    return response.data.data;
  },

  /**
   * Get single recipe by ID with full details
   */
  async getById(id: string): Promise<RecipeDetailResponse> {
    const response = await apiClient.get<ApiSuccess<RecipeDetailResponse>>(`/recipes/${id}`);
    return response.data.data;
  },

  /**
   * Create a new recipe
   */
  async create(data: CreateRecipeInput): Promise<RecipeCreateResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeCreateResponse>>('/recipes', data);
    return response.data.data;
  },

  /**
   * Update a recipe (creates a new version)
   */
  async update(id: string, data: UpdateRecipeInput): Promise<RecipeUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<RecipeUpdateResponse>>(`/recipes/${id}`, data);
    return response.data.data;
  },

  /**
   * Archive (soft delete) a recipe
   */
  async archive(id: string): Promise<RecipeArchiveResponse> {
    const response = await apiClient.delete<ApiSuccess<RecipeArchiveResponse>>(`/recipes/${id}`);
    return response.data.data;
  },

  /**
   * Duplicate a recipe
   */
  async duplicate(id: string, data?: DuplicateRecipeInput): Promise<RecipeDuplicateResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeDuplicateResponse>>(`/recipes/${id}/duplicate`, data || {});
    return response.data.data;
  },

  /**
   * Get version history for a recipe
   */
  async getVersions(id: string): Promise<RecipeVersionsResponse> {
    const response = await apiClient.get<ApiSuccess<RecipeVersionsResponse>>(`/recipes/${id}/versions`);
    return response.data.data;
  },

  /**
   * Get all versions by recipe code
   */
  async getByCode(code: string): Promise<RecipesByCodeResponse> {
    const response = await apiClient.get<ApiSuccess<RecipesByCodeResponse>>(`/recipes/by-code/${code}`);
    return response.data.data;
  },

  /**
   * Calculate and cache recipe cost
   */
  async calculateCost(id: string): Promise<RecipeCostResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeCostResponse>>(`/recipes/${id}/calculate-cost`);
    return response.data.data;
  },

  /**
   * Get recipe statistics
   */
  /**
   * Vue a plat : toutes les recettes actives et leurs ingredients
   */
  async getFlatView(): Promise<RecipeFlatViewResponse> {
    const response = await apiClient.get<ApiSuccess<RecipeFlatViewResponse>>('/recipes/flat-view');
    return response.data.data;
  },

  async getStats(): Promise<RecipeStatsResponse> {
    const response = await apiClient.get<ApiSuccess<RecipeStatsResponse>>('/recipes/stats');
    return response.data.data;
  },

  /**
   * Add ingredient to recipe (creates new version)
   */
  async addIngredient(recipeId: string, data: AddIngredientInput): Promise<RecipeDetailResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeDetailResponse>>(`/recipes/${recipeId}/ingredients`, data);
    return response.data.data;
  },

  /**
   * Update ingredient (creates new version)
   */
  async updateIngredient(recipeId: string, ingredientId: string, data: UpdateIngredientInput): Promise<RecipeDetailResponse> {
    const response = await apiClient.put<ApiSuccess<RecipeDetailResponse>>(`/recipes/${recipeId}/ingredients/${ingredientId}`, data);
    return response.data.data;
  },

  /**
   * Remove ingredient (creates new version)
   */
  async removeIngredient(recipeId: string, ingredientId: string): Promise<RecipeDetailResponse> {
    const response = await apiClient.delete<ApiSuccess<RecipeDetailResponse>>(`/recipes/${recipeId}/ingredients/${ingredientId}`);
    return response.data.data;
  },

  /**
   * Reorder ingredients (creates new version)
   */
  async reorderIngredients(recipeId: string, ingredientIds: string[]): Promise<RecipeDetailResponse> {
    const response = await apiClient.put<ApiSuccess<RecipeDetailResponse>>(`/recipes/${recipeId}/ingredients/reorder`, { ingredientIds });
    return response.data.data;
  },

  /**
   * Add a POS product to a recipe
   */
  async addPosProduct(recipeId: string, posProductId: string): Promise<RecipeDetailResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeDetailResponse>>(`/recipes/${recipeId}/pos-products`, { posProductId });
    return response.data.data;
  },

  /**
   * Remove a POS product from a recipe
   */
  async removePosProduct(recipeId: string, posProductId: string): Promise<RecipeDetailResponse> {
    const response = await apiClient.delete<ApiSuccess<RecipeDetailResponse>>(`/recipes/${recipeId}/pos-products/${posProductId}`);
    return response.data.data;
  },

  // ========================================
  // SPRINT 6 - DRAFT METHODS
  // ========================================

  // Sprint 6: createDraft() removed - create() always creates a draft now

  /**
   * Update an existing draft (direct modification, no new version)
   */
  async updateDraft(id: string, data: UpdateRecipeInput): Promise<RecipeUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<RecipeUpdateResponse>>(`/recipes/${id}/draft`, data);
    return response.data.data;
  },

  /**
   * Publish a draft (assigns version number, closes previous version)
   */
  async publish(id: string, data?: PublishRecipeInput): Promise<RecipeUpdateResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeUpdateResponse>>(`/recipes/${id}/publish`, data || {});
    return response.data.data;
  },

  /**
   * Delete a draft (only drafts can be deleted)
   */
  async deleteDraft(id: string): Promise<DeleteDraftResponse> {
    const response = await apiClient.delete<ApiSuccess<DeleteDraftResponse>>(`/recipes/${id}/draft`);
    return response.data.data;
  },

  /**
   * Create a draft from an active published version
   */
  async createDraftFromActive(id: string): Promise<RecipeCreateResponse> {
    const response = await apiClient.post<ApiSuccess<RecipeCreateResponse>>(`/recipes/${id}/create-draft`);
    return response.data.data;
  },

  /**
   * Recalculate cost for all active recipes
   */
  async recalculateAllCosts(): Promise<RecipesRecalculateAllResponse> {
    const response = await apiClient.post<ApiSuccess<RecipesRecalculateAllResponse>>('/recipes/recalculate-all');
    return response.data.data;
  },
};

export const posProductsWithoutRecipeService = {
  /**
   * Get POS products without an associated recipe
   */
  async list(): Promise<PosProductsWithoutRecipeResponse> {
    const response = await apiClient.get<ApiSuccess<PosProductsWithoutRecipeResponse>>('/pos-products/without-recipe');
    return response.data.data;
  },
};
